/**
 * Browser speech recognition, as a hook.
 *
 * Wraps the Web Speech API (`SpeechRecognition` / `webkitSpeechRecognition`).
 * NO NEW DEPENDENCIES: the API is in Chrome, Edge, and recent Safari, and the
 * fallback for everything else is typing, which is the same flow.
 *
 * THREE THINGS THIS HOOK EXISTS TO GET RIGHT.
 *
 * 1. SUPPORT IS DETECTED, NEVER ASSUMED. `supported` is false on Firefox and on
 *    any browser that has the constructor but refuses the microphone. The page
 *    shows a textarea instead and says why, rather than a mic button that does
 *    nothing when pressed.
 *
 * 2. FINAL AND INTERIM TEXT ARE DIFFERENT THINGS. The recogniser revises what
 *    it thinks it heard until it commits. Appending interim results to the
 *    transcript produces duplicated half-sentences, which is the classic bug in
 *    a naive integration. Finals go to `onFinal`; interim text is exposed
 *    separately for the greyed live line and is discarded when it is superseded.
 *
 * 3. CONTINUOUS MODE HAS TO RESTART ITSELF. Chrome ends a continuous session
 *    after a few seconds of silence and fires `onend`. A rep dictating with a
 *    pause in it expects the mic to still be on, so continuous mode restarts
 *    until the person stops it. Push-to-talk does NOT restart: the button being
 *    released means stop.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/** Minimal shape of the browser API, so this file needs no ambient types. */
interface SpeechRecognitionAlternativeLike { transcript: string; confidence: number }
interface SpeechRecognitionResultLike {
  isFinal: boolean
  length: number
  [index: number]: SpeechRecognitionAlternativeLike
}
interface SpeechRecognitionResultListLike {
  length: number
  [index: number]: SpeechRecognitionResultLike
}
interface SpeechRecognitionEventLike {
  resultIndex: number
  results: SpeechRecognitionResultListLike
}
interface SpeechRecognitionErrorEventLike { error: string; message?: string }
interface SpeechRecognitionLike {
  lang: string
  continuous: boolean
  interimResults: boolean
  maxAlternatives: number
  start(): void
  stop(): void
  abort(): void
  onresult: ((e: SpeechRecognitionEventLike) => void) | null
  onerror: ((e: SpeechRecognitionErrorEventLike) => void) | null
  onend: (() => void) | null
  onstart: (() => void) | null
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike

function getConstructor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor
    webkitSpeechRecognition?: SpeechRecognitionCtor
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

/** Plain-English text for each error the API reports. */
export function describeSpeechError(code: string): string {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'The browser blocked the microphone. Allow microphone access for this site, then try again.'
    case 'no-speech':
      return 'Nothing was heard. Check the microphone and try again.'
    case 'audio-capture':
      return 'No microphone was found.'
    case 'network':
      return 'Speech recognition needs a network connection and could not reach the service.'
    case 'aborted':
      return 'Listening stopped.'
    default:
      return `Speech recognition stopped (${code}).`
  }
}

export interface UseSpeechRecognitionOptions {
  lang?: string
  /** Called with each committed phrase. Never called with interim text. */
  onFinal?: (text: string) => void
}

export interface SpeechRecognitionState {
  /** False on a browser with no Web Speech API. Show the textarea instead. */
  supported: boolean
  listening: boolean
  /** True while a continuous session is on. */
  continuous: boolean
  /** The uncommitted phrase, for the greyed live line. */
  interim: string
  /** Set when the API reported a problem. Cleared on the next start. */
  error: string | null
  /**
   * The API's own error code behind `error`, for a caller that has to branch on
   * WHICH failure it was - a denied permission is recoverable and a missing API
   * is not. Additive: every existing consumer reads `error` and is unaffected.
   */
  errorCode: string | null
  start: (mode?: 'push' | 'continuous') => void
  stop: () => void
}

export function useSpeechRecognition(
  options: UseSpeechRecognitionOptions = {},
): SpeechRecognitionState {
  const ctor = useMemo(() => getConstructor(), [])
  const supported = ctor !== null

  const [listening, setListening] = useState(false)
  const [continuous, setContinuous] = useState(false)
  const [interim, setInterim] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)
  // Refs, not state, because the API's callbacks fire outside React's render
  // and must see the CURRENT intent rather than the value captured when the
  // handler was attached.
  const continuousRef = useRef(false)
  const wantListeningRef = useRef(false)
  const onFinalRef = useRef(options.onFinal)
  onFinalRef.current = options.onFinal
  const langRef = useRef(options.lang ?? 'en-US')
  langRef.current = options.lang ?? 'en-US'

  const stop = useCallback(() => {
    wantListeningRef.current = false
    continuousRef.current = false
    setContinuous(false)
    const rec = recognitionRef.current
    if (rec) {
      try { rec.stop() } catch { /* already stopped */ }
    }
    setListening(false)
    setInterim('')
  }, [])

  const start = useCallback((mode: 'push' | 'continuous' = 'push') => {
    if (!ctor) return
    setError(null)
    setErrorCode(null)
    // Restarting cleanly beats reusing a session the browser may have ended.
    const existing = recognitionRef.current
    if (existing) {
      try { existing.abort() } catch { /* ignore */ }
    }

    const rec = new ctor()
    rec.lang = langRef.current
    rec.continuous = mode === 'continuous'
    rec.interimResults = true
    rec.maxAlternatives = 1

    rec.onstart = () => {
      setListening(true)
    }

    rec.onresult = (event) => {
      let pending = ''
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i]
        if (!result) continue
        const text = result[0]?.transcript ?? ''
        if (!text) continue
        // Rule 2: only a FINAL result is committed. Interim text is a guess the
        // recogniser is still revising, and appending it duplicates phrases.
        if (result.isFinal) onFinalRef.current?.(text.trim())
        else pending += text
      }
      setInterim(pending.trim())
    }

    rec.onerror = (event) => {
      // `no-speech` in continuous mode is the recogniser reporting a pause, not
      // a failure. Surfacing it would flash an error at somebody mid-thought.
      if (event.error === 'no-speech' && continuousRef.current) return
      if (event.error === 'aborted') return
      setError(describeSpeechError(event.error))
      setErrorCode(event.error)
      wantListeningRef.current = false
      continuousRef.current = false
      setContinuous(false)
      setListening(false)
    }

    rec.onend = () => {
      setInterim('')
      // Rule 3: continuous mode restarts through the browser's own silence
      // timeout. Push-to-talk does not: the release meant stop.
      if (continuousRef.current && wantListeningRef.current) {
        try { rec.start() } catch { setListening(false) }
        return
      }
      setListening(false)
    }

    recognitionRef.current = rec
    wantListeningRef.current = true
    continuousRef.current = mode === 'continuous'
    setContinuous(mode === 'continuous')

    try {
      rec.start()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The microphone could not be started.')
      setErrorCode('start-failed')
      wantListeningRef.current = false
      setListening(false)
    }
  }, [ctor])

  // Abort on unmount, or the mic stays live after the page is gone.
  useEffect(() => () => {
    wantListeningRef.current = false
    continuousRef.current = false
    const rec = recognitionRef.current
    if (rec) {
      try { rec.abort() } catch { /* ignore */ }
    }
  }, [])

  return { supported, listening, continuous, interim, error, errorCode, start, stop }
}
